import {
    translation,
    type Translatable,
    type TranslatableValueFactoryParams,
} from "@towni/common";
import React, { createContext, useCallback, useRef, useState } from "react";
import { ButtonDanger } from "../buttons_v2/button-danger";
import { ButtonLightGrayBlack } from "../buttons_v2/button-gray";
import { ButtonPrimary } from "../buttons_v2/button-primary";
import { HorizontalDivider } from "../dividers";
import { FlexRow } from "../flex-containers";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../shadcdn/components/ui/alert-dialog";
import type { ParagraphContent } from "../text/paragraphs/paragraph";
import { Paragraph } from "../text/paragraphs/paragraph";

const defaultConfirmButtonText = translation({
    sv: "Ja",
    en: "Yes",
});
const defaultCancelButtonText = translation({
    sv: "Nej",
    en: "No",
});

type ConfirmDialogContextState = {
    title: Translatable | TranslatableValueFactoryParams;
    body: ParagraphContent | React.JSX.Element;
    cancelButtonText: ParagraphContent;
    confirmButtonText: ParagraphContent;
    level?: "danger" | "default";
    open: boolean;
};

type ConfirmParams = {
    title: ConfirmDialogContextState["title"];
    message?: ConfirmDialogContextState["body"];
    cancelBtnText?: ConfirmDialogContextState["cancelButtonText"];
    confirmBtnText?: ConfirmDialogContextState["confirmButtonText"];
    theme?: ConfirmDialogContextState["level"];
};

const ConfirmDialogContext = createContext<
    (params: ConfirmParams) => Promise<boolean>
>(() => null!);

const ConfirmDialogContextProvider = (props: {
    readonly children: React.ReactNode;
}) => {
    const [level, setLevel] = useState<"danger" | "default">("default");
    const [title, setTitle] = useState<
        Translatable | TranslatableValueFactoryParams
    >(null);
    const [body, setBody] = useState<ParagraphContent | React.JSX.Element>(
        null,
    );
    const [cancelButtonText, setCancelButtonText] =
        useState<ParagraphContent>(null);
    const [confirmButtonText, setConfirmButtonText] =
        useState<ParagraphContent>(null);
    const [open, setOpen] = useState(false);

    const confirmationRef =
        useRef<(confirmed: boolean | Promise<boolean>) => void>(null);
    const confirm = useCallback((value?: boolean) => {
        confirmationRef.current?.(value ?? true);
        setOpen(false);
    }, []);
    const dialog = useCallback(async (params: ConfirmParams) => {
        setTitle(params.title);
        setBody(params.message);
        setCancelButtonText(params.cancelBtnText ?? defaultCancelButtonText);
        setConfirmButtonText(params.confirmBtnText ?? defaultConfirmButtonText);
        setLevel(params.theme ?? "default");
        setOpen(true);

        return new Promise<boolean>((resolve, _reject) => {
            confirmationRef.current = resolve;
        });
    }, []);

    const ActionButton = level === "danger" ? ButtonDanger : ButtonPrimary;

    return (
        <ConfirmDialogContext.Provider value={dialog}>
            {props.children}
            <AlertDialog
                open={open}
                onOpenChange={open => {
                    if (!open) close();
                    return;
                }}>
                <AlertDialogContent asChild>
                    <form
                        onSubmit={event => {
                            event.preventDefault();
                            confirm(event.currentTarget.prompt?.value);
                        }}>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                <Paragraph content={title} />
                            </AlertDialogTitle>
                            {body ? (
                                <AlertDialogDescription>
                                    {React.isValidElement(body) ? (
                                        <>{body}</>
                                    ) : (
                                        <Paragraph
                                            content={
                                                body as unknown as ParagraphContent
                                            }
                                        />
                                    )}
                                </AlertDialogDescription>
                            ) : null}
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <FlexRow>
                                <ButtonLightGrayBlack
                                    padding={{
                                        all: 7,
                                        leftRight: 15,
                                    }}
                                    onClick={() => {
                                        confirm(false);
                                    }}>
                                    <Paragraph content={cancelButtonText} />
                                </ButtonLightGrayBlack>
                                <HorizontalDivider />
                                <ActionButton
                                    padding={{
                                        all: 7,
                                        leftRight: 15,
                                    }}
                                    onClick={() => {
                                        confirm();
                                    }}>
                                    <Paragraph content={confirmButtonText} />
                                </ActionButton>
                            </FlexRow>
                        </AlertDialogFooter>
                    </form>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmDialogContext.Provider>
    );
};

export { ConfirmDialogContext, ConfirmDialogContextProvider };
